import { Box, Container, Typography } from '@mui/material';
import faqQuestion from 'assets/images/faqQuestion.svg';
import Link from 'components/Link';
import { Accordion } from 'components/v2/common/Accordion';
import { RoleBox } from 'components/v2/common/RoleBox';
import { ReactNode, useState } from 'react';

interface Faq {
  title: string;
  content: ReactNode;
}

const investorFaqs: Faq[] = [
  {
    title: 'How does POD facilitates startup investing?',
    content:
      'POD connects promising startups with Angel investors, HNIs and VCs among others. All startups are vetted by our in-house market research specialists before presenting to our signed up investors. After the clearance from the market research specialists, the startups are given the chance to make their business pitch to potential investors. And if the investors find potential in the startup, they can choose to invest in them in return for equity on the platform itself.',
  },
  {
    title: 'Who can be an investor on POD?',
    content:
      'Any Indian citizen who is 21 years of age and above and has a significant understanding/knowledge of investment can be an investor on POD.',
  },
  {
    title: `What's the minimum amount of investment one can start with on POD?`,
    content: 'The minimum amount one can invest through POD is ₹20,000/-.',
  },
  {
    title: `How does the entire process of Startup investing work on POD?`,
    content: (
      <>
        <Box component="span">
          Here is all you need to know about startup investing on POD{' '}
        </Box>
        <Link
          noLinkStyle
          target="_blank"
          href="/pod-cafe/become-angel-investor-with-less-money"
        >
          Startup Investing Process
        </Link>
        <ul>
          <li>
            Create Profile: Kickstart your angel investing journey by creating a
            profile on POD.
          </li>
          <li>
            Take Assessment Test: Before becoming an investor on POD, investors
            have to answer a questionnaire to verify their understanding of
            investment risks in startups.
          </li>
          <li>
            KYC Verification: Once the KYC of the investor is successfully
            verified, they will be onboarded on POD and get access to vetted
            startups to invest in.
          </li>
          <li>
            Invest in Startups: Once the investor finds a startup that&apos;s
            aligned with their vision and interest on &quot;Invest&quot; page,
            they can go right ahead and invest with a single click.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: `How much does POD charge for each investment you make through the platform?`,
    content:
      'As of now, POD levies no commission from investors on their investment made through the platform. The primary focus is to create a reliable and transparent ecosystem for investors in promising startups in India.',
  },
  {
    title: `What kind of startups can one invest on POD?`,
    content:
      'POD lists only promising and fully vetted startups from verticals ranging across Tech, Fintech, Social Entrepreneurship, Lifestyle and even those offering SaaS. Startups need to be in existence for at least 6 months and show significant month over month growth to fall in the initial listing criteria on POD.',
  },
  {
    title: `What if the startup doesn't reach the funding goal? What will happen to my money?`,
    content: (
      <>
        When a startup reaches its funding goal, we transfer the amount received
        from the investor to the startup&apos;s account, and issue the Share
        Certificate in the name of the investor.
        <br />
        However, on the downside, if a startup fails to reach the set funding
        target of its goal, it is unlikely that they can act on their proposed
        business plan. Hence, to protect the investor&apos;s funds, the raised
        funds are refunded.
        <br />
        Therefore, a startup either receives 100% of the funds when it reaches
        its funding goal, or nothing at all when it fails to.
      </>
    ),
  },
  {
    title: `How liquid will my investment be in a startup?`,
    content: (
      <>
        Since there is no platform or exchange body where securities of the
        startup trade or can sell out, your investment remains highly illiquid.
        You get to take an exit only on the occurrence of events like LBO/ M&A/
        IPO or at the time of selling or winding up of the company.
      </>
    ),
  },
  {
    title: `How can investors track the performance/progress of the startups they invested in?`,
    content: (
      <>
        Investors can log in to their{' '}
        <Link noLinkStyle href="/investor-portfolio" target="_blank">
          Dashboard
        </Link>
        &nbsp;and access all the details from the Portfolio section on POD,
        including the progress of the funded campaigns.
      </>
    ),
  },
  {
    title: `Will the team at POD also offer advice on how to yield the maximum return on investments? Or point an investor towards the most promising startups?`,
    content: (
      <>
        We, at POD, take full responsibility to provide our investors with all
        the information related to the startup which will enable investors to
        make an informed decision.
        <br />
        The investors are liable for all their investment choices, and POD will
        steer clear of any advisory services at all times.
      </>
    ),
  },
];

const startupFaqs: Faq[] = [
  {
    title: `What is the minimum and maximum funds a startup can raise on POD?`,
    content: (
      <>
        As of now, a startup can raise funds ranging from INR 20 Lakhs to INR
        1Cr. on POD.
      </>
    ),
  },
  {
    title: `What kind of startups can register to raise funds through POD?`,
    content: (
      <>
        There is no restriction to the type of business or sector of startups.
        The sole criteria required that the product/service or offering from the
        startup should be unique and comes with a good potential market to cater
        to.
      </>
    ),
  },
  {
    title: `How does the entire process of fundraising work on POD?`,
    content: (
      <>
        Here is all you need to know about raising funds on POD -<br />
        <strong>Startup Onboarding</strong>
        <ul>
          <li>Create Profile</li>
          <li>
            Onboarding: As the first step, a startup founder is asked to share
            the relevant details about his business and other KYC requirements.
            These details are collected through a form and sent to the team for
            vetting and due diligence.
          </li>
          <li>
            Due diligence: The team verifies all the business details, vets KYC
            etc. to ensure that the information submitted by the startup is
            legitimate. Only after this will the startup be launched on POD.
          </li>
          <li>
            Fundraising Campaign: Once the startup clears the vetting process, a
            fundraising campaign with a specific goal amount is launched on POD.
          </li>
        </ul>
        <strong>Investment and Fund/share transfer</strong>
        <ul>
          <li>
            Direct Investment: Once the investor finds a{' '}
            <Link href="/startups" target="_blank" noLinkStyle>
              startup on POD
            </Link>{' '}
            that&apos;s aligned with their vision and interest, they can go
            right ahead and invest with a single click. Different modes of
            payment are available for investors to choose from - Net banking,
            Debit Card, etc.
          </li>
          <li>
            Campaign Status: When an investor invests in a startup, the funds
            remain in POD&apos;s Escrow account. Each startup will be given
            14-21 days to raise funds from POD.
          </li>
          <li>
            End of Campaign: If the startup reaches its funding goal, the amount
            is transferred to the startup and Share Certificate will be issued
            to the investor. If not, the funds will be refunded to the investor
            within 7 working days. Post this, the campaign is marked as closed.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: `What is the eligibility criteria for a startup to register on POD?`,
    content: (
      <>
        A startup should be:
        <ol>
          <li>
            Registered in India and incorporated not more than 3 years ago
          </li>
          <li>
            Execute a unique business idea with a reasonable size target market
          </li>
          <li> At least have an MVP ready</li>
        </ol>
      </>
    ),
  },
  {
    title: `On what basis POD vets the startups?`,
    content: (
      <>
        The team at POD performs complete due diligence to ensure that the
        details submitted by the startup are legal and valid. The critical areas
        against which POD vet the startups are -
        <ul>
          <li>Competent Team and Advisors Background</li>
          <li>Business Idea (Business Model and its USPs)</li>
          <li>Traction </li>
          <li>Financials</li>
          <li>User experience/competitive advantages</li>
        </ul>
        <br />
        The vetting process is expected to be completed in 7 business days.
        <br />
        Should a startup not qualify against the vetting process, the team will
        reach out with updates on how to rectify areas of concern before they
        can reapply.
      </>
    ),
  },
  {
    title: `Can a startup re-register after getting rejected on POD?`,
    content: (
      <>
        Yes. The startup can rectify the shortcomings from the first submission
        and re-register on the platform after 3 months.
      </>
    ),
  },
  {
    title: `How POD facilitates startup fundraising?`,
    content: (
      <>
        POD allows startups to pitch their business ideas to potential
        investors. If investors are interested, they can invest in the startup
        directly through the platform in exchange for equity/convertibles.
      </>
    ),
  },
  {
    title: `What is the implication of a 'Funding Goal' on POD?`,
    content: (
      <>
        Every startup funding campaign begins with a specific amount that a
        startup wants to raise on POD, it is called &apos;Funding Goal&apos;.
        <br />
        When the startup able to raise the &apos;Funding Goal&apos; amount, we
        consider it as Funding Goal Reached. As a result, the amount will be
        transferred to startup account.
        <br />
        If the startup not able to achieve the &apos;Funding Goal&apos;,
        whatever amount that is raised so far, will be refunded to the investor.
      </>
    ),
  },
  {
    title: `When does a funding campaign ends on POD?`,
    content: (
      <>
        The funding campaign on POD ends on two conditions -
        <ul>
          <li>
            Firstly, when a startup&apos;s fundraising campaign accomplishes its
            goal on or before its end date - the raised amount is transferred to
            the startup&apos;s account and the campaign is closed.
          </li>
          <li>
            Secondly, when a startup fails to reach its goal in the stipulated
            time frame, the raised amount will be returned to the investor and
            the campaign is marked closed.
          </li>
        </ul>
        In both cases, a campaign closes by its end date on POD.
      </>
    ),
  },
  {
    title: `How can a startup track the fundraising progress of their campaign on POD?`,
    content: (
      <>
        A startup and view all the information about the fundraising, real-time,
        on Startup&apos;s Dashboard. In addition, each time when an investor
        invests funds, the startup will receive an email update on the
        registered email address.
      </>
    ),
  },
  {
    title: `What are the documents that a startup needs to submit to POD to complete the due diligence and vetting process?`,
    content: (
      <>
        <ul>
          <li>Company&apos;s PAN</li>
          <li>Incorporation certificate</li>
          <li>Financial Projections</li>
        </ul>
      </>
    ),
  },
];

const Faqs = () => {
  const [type, setType] = useState<'investors' | 'startups'>('investors');

  return (
    <Box bgcolor="#F6FBFB" pt={5} mb={10}>
      <Container>
        <Box
          bgcolor="#E6F0F1"
          display="flex"
          px={3}
          pb="19px"
          sx={{
            pt: {
              xs: 1.5,
              md: 5,
            },
            pb: {
              xs: 1.5,
              md: '19px',
            },
          }}
        >
          <Box display="flex" alignItems="center">
            <img src={faqQuestion.src} alt="FAQ icon" />
          </Box>
          <Box ml={1.5}>
            <Typography
              fontWeight={600}
              color="#FF6446"
              sx={{
                fontSize: {
                  xs: 16,
                  md: 24,
                },
              }}
            >
              Have a doubt?
            </Typography>
            <Typography
              component="h1"
              fontWeight={900}
              color="#004643"
              sx={{
                fontSize: {
                  xs: 24,
                  md: 40,
                },
              }}
            >
              Frequently Asked Questions
            </Typography>
          </Box>
        </Box>
        <Box display="flex" mt={3} mb="55px">
          <RoleBox
            sx={{
              bgcolor: type == 'investors' ? '#008B5E' : undefined,
              color: type == 'investors' ? '#fff' : undefined,
              fontSize: 16,
              py: '5px',
            }}
            onClick={() => setType('investors')}
          >
            Investors
          </RoleBox>
          <RoleBox
            sx={{
              bgcolor: type == 'startups' ? '#008B5E' : undefined,
              color: type == 'startups' ? '#fff' : undefined,
              fontSize: 16,
              py: '5px',
            }}
            onClick={() => setType('startups')}
          >
            Startups
          </RoleBox>
        </Box>
        <Box border="0.6px solid #008B5E" borderBottom="none">
          {type == 'investors'
            ? investorFaqs.map((item, i) => (
                <Accordion key={item.title} index={i} title={item.title}>
                  {item.content}
                </Accordion>
              ))
            : startupFaqs.map((item, i) => (
                <Accordion key={item.title} index={i} title={item.title}>
                  {item.content}
                </Accordion>
              ))}
          {/* // <Accordion />
          // <Accordion /> */}
        </Box>
      </Container>
    </Box>
  );
};

export default Faqs;
